import React, { useState, useRef } from "react";
import styled from "styled-components";
import { Snackbar, Alert } from "@mui/material";
import { FiMail, FiGithub, FiLinkedin, FiSend } from "react-icons/fi";
import { Bio } from "../../data/constants";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 60px 0 80px 0;
  @media (max-width: 960px) {
    padding: 40px 0 60px 0;
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 1150px;
  gap: 20px;
  padding: 0 20px;
`;

const Tag = styled.div`
  background: rgba(133, 76, 230, 0.12);
  border: 1px solid rgba(133, 76, 230, 0.4);
  color: #c084fc;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 6px 18px;
  border-radius: 9999px;
  box-shadow: 0 0 15px rgba(133, 76, 230, 0.2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 40px;
  width: 100%;
  margin-top: 10px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

/* ---------------------------------
   LEFT COLUMN
--------------------------------- */
const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Heading = styled.h2`
  font-size: clamp(2.4rem, 5vw, 3.8rem);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.03em;
  color: #ffffff;
  margin: 0;

  .gradient-text {
    background: linear-gradient(135deg, #854CE6 0%, #c026d3 55%, #e879f9 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: block;
    filter: drop-shadow(0 0 20px rgba(133, 76, 230, 0.4));
  }
`;

const Subtitle = styled.p`
  font-size: 15px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.65);
  margin: 0;
  max-width: 480px;
`;

const ContactInfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 8px;
`;

const ContactInfoCard = styled.a`
  display: flex;
  align-items: center;
  gap: 16px;
  background: ${({ theme }) => theme.card || "#171721"};
  border: 1px solid ${(props) => (props.highlight ? "rgba(133, 76, 230, 0.6)" : "rgba(255, 255, 255, 0.08)")};
  border-radius: 14px;
  padding: 16px 20px;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);

  &:hover {
    border-color: rgba(133, 76, 230, 0.8);
    transform: translateX(6px);
    box-shadow: 0 6px 24px rgba(133, 76, 230, 0.2);
  }

  ${(props) =>
    props.highlight &&
    `
    &::after {
      content: '';
      position: absolute;
      top: -4px;
      right: 24px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #854CE6;
      box-shadow: 0 0 10px #854CE6;
    }
  `}
`;

const IconBox = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: rgba(133, 76, 230, 0.15);
  border: 1px solid rgba(133, 76, 230, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c084fc;
  font-size: 20px;
  flex-shrink: 0;
`;

const CardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CardSubLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
`;

const CardValue = styled.span`
  font-size: 14.5px;
  font-weight: 600;
  color: #ffffff;
`;

/* ---------------------------------
   RIGHT COLUMN (FORM)
--------------------------------- */
const FormContainer = styled.div`
  background: ${({ theme }) => theme.card || "#171721"};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 36px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);

  @media (max-width: 640px) {
    padding: 24px 20px;
  }
`;

const FormTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 4px 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  width: 100%;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.65);
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 13px 16px;
  font-size: 14px;
  color: #ffffff;
  outline: none;
  font-family: inherit;
  transition: all 0.25s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    border-color: #854CE6;
    background: rgba(133, 76, 230, 0.05);
    box-shadow: 0 0 15px rgba(133, 76, 230, 0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 13px 16px;
  font-size: 14px;
  color: #ffffff;
  outline: none;
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  transition: all 0.25s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    border-color: #854CE6;
    background: rgba(133, 76, 230, 0.05);
    box-shadow: 0 0 15px rgba(133, 76, 230, 0.2);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #854CE6 0%, #a855f7 100%);
  border: none;
  border-radius: 10px;
  padding: 14px 20px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(133, 76, 230, 0.35);
  margin-top: 4px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(133, 76, 230, 0.55);
    filter: brightness(1.08);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    font-size: 16px;
  }
`;

const Contact = () => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(form.current);
    formData.append("access_key", "38aef2b9-6a25-46f1-9663-8fcce3a80282");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setOpen(true);
        form.current.reset();
      } else {
        alert(data.message || "Form submission failed");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Something went wrong!");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container id="contact">
      <Wrapper>
        <Tag>Get In Touch</Tag>

        <ContentGrid>
          {/* Left Column */}
          <LeftColumn>
            <Heading>
              LET'S BUILD
              <span className="gradient-text">SOMETHING GREAT</span>
              TOGETHER.
            </Heading>

            <Subtitle>
              Have a project, idea, or opportunity? Let's talk. I'm always open to
              discussing new engineering challenges and collaborations.
            </Subtitle>

            <ContactInfoList>
              <ContactInfoCard
                href="mailto:RaviiSingh733@gmail.com"
                highlight
              >
                <IconBox>
                  <FiMail />
                </IconBox>
                <CardDetails>
                  <CardSubLabel>Email Directly</CardSubLabel>
                  <CardValue>RaviiSingh733@gmail.com</CardValue>
                </CardDetails>
              </ContactInfoCard>

              <ContactInfoCard
                href={Bio.github || "https://github.com/RaviSingh733"}
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconBox>
                  <FiGithub />
                </IconBox>
                <CardDetails>
                  <CardSubLabel>Github Profile</CardSubLabel>
                  <CardValue>github.com/RaviSingh733</CardValue>
                </CardDetails>
              </ContactInfoCard>

              <ContactInfoCard
                href={Bio.linkedin || "https://www.linkedin.com/in/ravi-singh007"}
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconBox>
                  <FiLinkedin />
                </IconBox>
                <CardDetails>
                  <CardSubLabel>Linkedin Profile</CardSubLabel>
                  <CardValue>Connect on LinkedIn</CardValue>
                </CardDetails>
              </ContactInfoCard>
            </ContactInfoList>
          </LeftColumn>

          {/* Right Column (Form) */}
          <FormContainer>
            <FormTitle>Send a Direct Message</FormTitle>

            <Form ref={form} onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Your Name</Label>
                <Input
                  type="text"
                  placeholder="e.g. Ravi"
                  name="name"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Your Email</Label>
                <Input
                  type="email"
                  placeholder="e.g. Ravi@example.com"
                  name="email"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Subject</Label>
                <Input
                  type="text"
                  placeholder="Project Discussion / Opportunity"
                  name="subject"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Message</Label>
                <TextArea
                  placeholder="Tell me about your project, timeline, or idea..."
                  rows="4"
                  name="message"
                  required
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                <FiSend /> {isSubmitting ? "Sending..." : "Send Message →"}
              </SubmitButton>
            </Form>
          </FormContainer>
        </ContentGrid>

        <Snackbar
          open={open}
          autoHideDuration={6000}
          onClose={() => setOpen(false)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <Alert
            onClose={() => setOpen(false)}
            severity="success"
            sx={{ width: "100%" }}
          >
            Message Sent Successfully!
          </Alert>
        </Snackbar>
      </Wrapper>
    </Container>
  );
};

export default Contact;